import {dotComDataset} from '@/lib/dataset'
import {multilingualStringPreview} from '@/lib/multilingualArrayString'
import {defineArrayMember, PreviewConfig} from 'sanity'
import {match} from 'ts-pattern'

type KnownDotcomDocumentType =
  | 'ship'
  | 'destination'
  | 'subDestination'
  | 'cruise'
  | 'specialVoyage'
  | 'landProgramme'
  | 'country'
  | 'port'
  | 'cruisesTagCategory'
  | 'microContentSubscribeToNewsletter'
  | 'formDisclaimer'
  | 'requestBrochure'
  | 'page'
  | 'landingPage'

export const dotcomDocument = (type: KnownDotcomDocumentType) => ({
  ...dotcomDocuments([type]),
})

export const dotcomDocuments = (types: KnownDotcomDocumentType[]) =>
  defineArrayMember({
    type: 'crossDatasetReference' as const,
    dataset: dotComDataset,
    weak: true,
    options: {
      disableNew: true,
      filter: defaultFilterForDocument(types[0]!),
    },
    to: types.map((type) => ({
      type: type,
      preview: previewForDocument(type),
    })),
  })

export const crossDatabaseReference = (type: KnownDotcomDocumentType) =>
  defineArrayMember({
    type: 'crossDatasetReference' as const,
    dataset: dotComDataset,
    weak: true,
    options: {
      disableNew: true,
      filter: defaultFilterForDocument(type),
    },
    to: [
      {
        type: type,
        preview: previewForDocument(type),
      },
    ],
  })

const defaultFilterForDocument = (type: KnownDotcomDocumentType) => {
  return match(type)
    .with('ship', () => 'defined(metadata.routing.path.en)')
    .with('destination', () => 'defined(metadata.routing.path.en)')
    .with('subDestination', () => 'defined(metadata.routing.path.en)')
    .with('landProgramme', () => 'defined(port)')
    .with('cruise', () => 'defined(metadata.routing.path.en)')
    .with('specialVoyage', () => 'defined(metadata.routing.path.en)')
    .with('port', () => '')
    .with('country', () => '')
    .with('requestBrochure', () => 'defined(metadata.routing.path.en)')
    .with('page', () => 'defined(metadata.routing.path.en)')
    .with('landingPage', () => 'defined(metadata.routing.path.en)')
    .with('cruisesTagCategory', 'formDisclaimer', 'microContentSubscribeToNewsletter', () => '')
    .exhaustive()
}

export const previewForDocument = (type: KnownDotcomDocumentType) =>
  match(type)
    .returnType<PreviewConfig | undefined>()
    .with('ship', () => ({
      select: {
        title: 'name',
        media: 'card.image',
      },
    }))
    .with('destination', 'subDestination', () => ({
      select: {
        tt: 'name.langs[].value', // Note tt is only used to force sanity to query multilingual fields
        name: 'name',
        media: 'card.image',
      },
      prepare({name, media}) {
        return {
          title: multilingualStringPreview(name),
          media: media,
        }
      },
    }))
    .with('landProgramme', () => ({
      select: {
        title: 'name',
        subtitle: 'code',
        media: 'image',
      },
    }))
    .with('country', () => ({
      select: {
        tt: 'name.langs[].value', // Note tt is only used to force sanity to query multilingual fields
        title: 'name',
        subtitle: 'isoAlpha2Code',
      },
      prepare({title, subtitle}) {
        return {
          title: multilingualStringPreview(title),
          subtitle,
        }
      },
    }))
    .with('port', () => ({
      select: {
        tt: 'data.name.langs[].value', // Note tt is only used to force sanity to query multilingual fields
        tt2: 'name.langs[].value',
        tt3: 'region.name.langs[].value',
        name: 'data.name',
        nameOverride: 'name',
        code: 'data.code',
        region: 'region.name',
        country: 'data.country.name',
        media: 'card.image',
      },
      prepare({media, code, ...value}) {
        console.debug('port', value)
        const name = multilingualStringPreview(value.name)
        const nameOverride = multilingualStringPreview(value.nameOverride)
        const region = multilingualStringPreview(value.region)
        const country = multilingualStringPreview(value.country)
        const title = code ? `${nameOverride || name} (${code})` : nameOverride || name

        return {
          title: title,
          subtitle: region || country,
          media: media,
        }
      },
    }))
    .with('cruisesTagCategory', () => ({
      select: {
        tt: 'title.langs[].value', // Note tt is only used to force sanity to query multilingual fields
        title: 'title',
      },
      prepare({title}) {
        return {
          title: multilingualStringPreview(title),
        }
      },
    }))
    .with('cruise', 'specialVoyage', () => ({
      select: {
        cruiseCode: 'data.cruiseCode',
        departurePort: 'data.departurePort.name',
        arrivalPort: 'data.arrivalPort.name',
        mediaHero: 'heroImage',
        mediaCard: 'cardImage',
        departureDate: 'data.departureDate',
        cruiseCodeCopy: 'cruiseCode',
        name: 'name',
      },
      prepare({
        name,
        cruiseCode,
        departurePort,
        arrivalPort,
        mediaHero,
        mediaCard,
        departureDate,
        cruiseCodeCopy,
      }) {
        const localizedName = multilingualStringPreview(name)
        return {
          title: `${localizedName ? `${localizedName} - ` : ''} ${
            cruiseCode || `(deleted cruise) ${cruiseCodeCopy}`
          }`,
          subtitle:
            multilingualStringPreview(departurePort) && multilingualStringPreview(arrivalPort)
              ? `${departureDate} - ${departurePort} to ${arrivalPort}`
              : '(no data)',
          media: mediaHero || mediaCard,
        }
      },
    }))
    .with('microContentSubscribeToNewsletter', () => ({
      prepare() {
        return {
          title: 'Subscribe to newsletter',
        }
      },
    }))
    .with('formDisclaimer', () => ({
      prepare() {
        return {
          title: 'Form disclaimer',
        }
      },
    }))
    .with('requestBrochure', () => ({
      select: {
        media: 'card.image',
      },
      prepare: (props) => ({
        title: 'Request Brochure',
        media: props.media,
      }),
    }))
    .with('page', 'landingPage', () => ({
      select: {
        dotcomTitle: 'title',
        dotcomTitleCard: 'card.title',
        dotcomPath: 'metadata.routing.path.en',
        dotcomCardMedia: 'card.image',
        dotcomHeroImage: 'sections.0.background',
      },
      prepare({dotcomTitle, dotcomTitleCard, dotcomPath, dotcomCardMedia, dotcomHeroImage}) {
        const media = dotcomHeroImage || dotcomCardMedia
        return {
          title:
            multilingualStringPreview(dotcomTitleCard) || multilingualStringPreview(dotcomTitle),
          subtitle: toSilverseaComUrl(dotcomPath),
          media: media,
        }
      },
    }))
    .exhaustive()

export const toSilverseaComUrl = (slug: string | undefined) => {
  if (!slug) return undefined
  return `https://www.silversea.com${slug}`
}
